import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import WorkforceOtStatePolicyDrawer from './WorkforceOtStatePolicyDrawer';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonatePlainIconButtonSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';

const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const defaultForm = {
  countryCode: 'US',
  stateCode: '',
  stateName: '',
  timezone: '',
  dailyOvertimeSupported: false,
  weeklyOvertimeSupported: true,
  doubletimeSupported: false,
  seventhDaySupported: false,
  consecutiveHoursSupported: false,
  status: 'ACTIVE',
};

const mapStateRow = (item) => ({
  id: item.state_id || item.stateId,
  stateCode: item.state_code || item.stateCode,
  stateName: item.state_name || item.stateName,
  countryCode: item.country_code || item.countryCode,
  timezone: item.timezone || '',
  defaultPolicyName: item.default_policy_name || item.defaultPolicyName || '',
  dailyOvertimeSupported: Boolean(item.daily_overtime_supported ?? item.dailyOvertimeSupported),
  weeklyOvertimeSupported: Boolean(item.weekly_overtime_supported ?? item.weeklyOvertimeSupported),
  doubletimeSupported: Boolean(item.doubletime_supported ?? item.doubletimeSupported),
  seventhDaySupported: Boolean(item.seventh_day_supported ?? item.seventhDaySupported),
  consecutiveHoursSupported: Boolean(item.consecutive_hours_supported ?? item.consecutiveHoursSupported),
  status: item.status || 'ACTIVE',
  raw: item,
});

const yesNo = (value) => (value ? 'Yes' : 'No');

const yesNoChipCell = (params) => {
  const v = params.value;
  if (v !== 'Yes' && v !== 'No') return '';
  const isYes = v === 'Yes';
  return (
    <Chip
      label={v}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: isYes ? ht.secondaryTab : ht.listBg,
        color: isYes ? ht.text : ht.text,
        borderRadius: ht.buttonRadius,
        '& .MuiChip-label': { px: 1.5 },
      }}
    />
  );
};

const WorkforceOtState = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.WORKFORCE_OT_STATE);
  const { toastState, showSuccess, showError, hideToast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerState, setDrawerState] = useState(null);

  const selectedRow = useMemo(
    () => rows.find((row) => row.id === selectionModel[0]),
    [rows, selectionModel]
  );

  const fetchStates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch('conductor/workforce-state?include_inactive=true');
      setRows((Array.isArray(data) ? data : []).map(mapStateRow));
    } catch (err) {
      setRows([]);
      showError(err.message || 'Failed to load workforce OT states');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchStates();
  }, [fetchStates]);

  const columns = useMemo(() => [
    { field: 'stateCode', headerName: 'State Code', width: 110, resizable: true },
    { field: 'stateName', headerName: 'State Name', width: 200, resizable: true },
    { field: 'countryCode', headerName: 'Country', width: 90, resizable: true },
    { field: 'defaultPolicyName', headerName: 'Policy Name', width: 180, resizable: true },
    { field: 'dailyOvertimeSupported', headerName: 'Daily OT', width: 90, resizable: true, valueGetter: (params) => yesNo(params.row?.dailyOvertimeSupported), renderCell: yesNoChipCell },
    { field: 'weeklyOvertimeSupported', headerName: 'Weekly OT', width: 100, resizable: true, valueGetter: (params) => yesNo(params.row?.weeklyOvertimeSupported), renderCell: yesNoChipCell },
    { field: 'doubletimeSupported', headerName: 'Double Time', width: 110, resizable: true, valueGetter: (params) => yesNo(params.row?.doubletimeSupported), renderCell: yesNoChipCell },
    { field: 'seventhDaySupported', headerName: '7th Day', width: 90, resizable: true, valueGetter: (params) => yesNo(params.row?.seventhDaySupported), renderCell: yesNoChipCell },
    { field: 'consecutiveHoursSupported', headerName: 'Consecutive Hrs', width: 130, resizable: true, valueGetter: (params) => yesNo(params.row?.consecutiveHoursSupported), renderCell: yesNoChipCell },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      resizable: true,
      renderCell: (params) => ((params.value || '') === '' ? '' : String(params.value).toUpperCase()),
    },
  ], []);

  const buildPayload = () => ({
    country_code: form.countryCode?.trim() || 'US',
    state_code: form.stateCode?.trim(),
    state_name: form.stateName?.trim(),
    timezone: form.timezone?.trim() || null,
    daily_overtime_supported: Boolean(form.dailyOvertimeSupported),
    weekly_overtime_supported: Boolean(form.weeklyOvertimeSupported),
    doubletime_supported: Boolean(form.doubletimeSupported),
    seventh_day_supported: Boolean(form.seventhDaySupported),
    consecutive_hours_supported: Boolean(form.consecutiveHoursSupported),
    status: form.status || 'ACTIVE',
  });

  const openAdd = () => {
    if (!canAdd) return;
    setMode('add');
    setEditingId(null);
    setForm({ ...defaultForm });
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!canEdit || !selectedRow) return;
    setMode('edit');
    setEditingId(selectedRow.id);
    setForm({
      countryCode: selectedRow.countryCode || 'US',
      stateCode: selectedRow.stateCode || '',
      stateName: selectedRow.stateName || '',
      timezone: selectedRow.timezone || '',
      dailyOvertimeSupported: selectedRow.dailyOvertimeSupported,
      weeklyOvertimeSupported: selectedRow.weeklyOvertimeSupported,
      doubletimeSupported: selectedRow.doubletimeSupported,
      seventhDaySupported: selectedRow.seventhDaySupported,
      consecutiveHoursSupported: selectedRow.consecutiveHoursSupported,
      status: selectedRow.status || 'ACTIVE',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if ((mode === 'add' && !canAdd) || (mode === 'edit' && !canEdit)) return;
    try {
      setSubmitting(true);
      const payload = buildPayload();
      if (mode === 'add') {
        await apiFetch('conductor/workforce-state', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        showSuccess('Workforce OT state saved successfully');
      } else {
        await apiFetch(`conductor/workforce-state/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        showSuccess('Workforce OT state updated successfully');
      }
      setDialogOpen(false);
      setSelectionModel([]);
      fetchStates();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      await apiFetch(`conductor/workforce-state/${rowPendingDelete.id}`, { method: 'DELETE' });
      showSuccess('Workforce OT state deleted');
      setDeleteOpen(false);
      setRowPendingDelete(null);
      setSelectionModel([]);
      if (drawerState?.id === rowPendingDelete.id) {
        setDrawerOpen(false);
        setDrawerState(null);
      }
      fetchStates();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRowClick = (params) => {
    setDrawerState(params.row);
    setDrawerOpen(true);
  };

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>Workforce OT State</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={canEdit ? 'Edit State' : 'No edit permission'}><span><IconButton onClick={openEdit} disabled={!selectedRow || !canEdit} sx={harmonatePlainIconButtonSx}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canAdd ? 'Add State' : 'No add permission'}><span><IconButton onClick={openAdd} disabled={!canAdd} sx={harmonatePlainIconButtonSx}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
          <Tooltip title={canDelete ? 'Delete State' : 'No delete permission'}><span><IconButton onClick={() => { setRowPendingDelete(selectedRow); setDeleteOpen(true); }} disabled={!selectedRow || !canDelete} sx={harmonatePlainIconButtonSx}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          loading={loading}
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          onRowClick={handleRowClick}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>{mode === 'add' ? 'Add Workforce OT State' : 'Edit Workforce OT State'}</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" required label="Country Code" value={form.countryCode} onChange={(e) => setForm((p) => ({ ...p, countryCode: e.target.value }))} />
            <TextField fullWidth size="small" required label="State Code" value={form.stateCode} onChange={(e) => setForm((p) => ({ ...p, stateCode: e.target.value }))} />
            <TextField fullWidth size="small" required label="State Name" value={form.stateName} onChange={(e) => setForm((p) => ({ ...p, stateName: e.target.value }))} />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth size="small" label="Timezone" value={form.timezone} onChange={(e) => setForm((p) => ({ ...p, timezone: e.target.value }))} />
            <TextField fullWidth size="small" select label="Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} SelectProps={{ MenuProps: harmonateSelectMenuProps }}>
              <MenuItem value="ACTIVE">ACTIVE</MenuItem>
              <MenuItem value="INACTIVE">INACTIVE</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <FormControlLabel control={<Checkbox checked={form.dailyOvertimeSupported} onChange={(e) => setForm((p) => ({ ...p, dailyOvertimeSupported: e.target.checked }))} />} label="Daily OT Supported" />
            <FormControlLabel control={<Checkbox checked={form.weeklyOvertimeSupported} onChange={(e) => setForm((p) => ({ ...p, weeklyOvertimeSupported: e.target.checked }))} />} label="Weekly OT Supported" />
            <FormControlLabel control={<Checkbox checked={form.doubletimeSupported} onChange={(e) => setForm((p) => ({ ...p, doubletimeSupported: e.target.checked }))} />} label="Double Time Supported" />
            <FormControlLabel control={<Checkbox checked={form.seventhDaySupported} onChange={(e) => setForm((p) => ({ ...p, seventhDaySupported: e.target.checked }))} />} label="7th Day Supported" />
            <FormControlLabel control={<Checkbox checked={form.consecutiveHoursSupported} onChange={(e) => setForm((p) => ({ ...p, consecutiveHoursSupported: e.target.checked }))} />} label="Consecutive Hours Supported" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={submitting} sx={harmonateProceedButtonSx}>
            {submitting ? 'Saving...' : mode === 'add' ? 'Save' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => !deleteLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Delete workforce OT state &quot;{rowPendingDelete?.stateName || 'selected'}&quot;?
            This cannot be undone. States assigned to properties cannot be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" disabled={deleteLoading} sx={harmonateAbortButtonSx}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <WorkforceOtStatePolicyDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerState(null); }}
        stateRow={drawerState}
      />

      <Toast open={toastState.open} onClose={hideToast} message={toastState.message} severity={toastState.severity} title={toastState.title} autoHideDuration={toastState.autoHideDuration} position={toastState.position} />
    </Box>
  );
};

export default WorkforceOtState;
